import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '@siaedts/shared/lib/supabase';

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  publishedAt: string;
  readTime: string;
  featured: boolean;
  image?: string;
  tags: string[];
}

interface RemoteNewsArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string | null;
  category?: 'company' | 'industry' | 'launches' | 'events' | 'tips' | null;
  featured_image_url?: string | null;
  featured: boolean;
  published_at?: string | null;
  author?: { name: string } | null;
}

const categories = ['all', 'company', 'industry', 'launches', 'events', 'tips'];

export const mockNews: NewsArticle[] = [
  {
    id: '1',
    title: 'SIAEDTS-SARL Expands Partnership with John Deere',
    slug: 'siaedts-expands-john-deere-partnership',
    excerpt: 'We are excited to announce our expanded partnership with John Deere, bringing the latest precision agriculture technology to our customers across the region.',
    content: 'Full article content here...',
    category: 'company',
    author: 'Sarah Johnson',
    publishedAt: '2026-08-15',
    readTime: '5 min read',
    featured: true,
    tags: ['Partnership', 'John Deere', 'Precision Ag'],
  },
  {
    id: '2',
    title: 'New Valley Irrigation Systems Now Available',
    slug: 'new-valley-irrigation-systems',
    excerpt: 'The latest Valley center pivot and linear irrigation systems are now in stock. Featuring ICON smart controls for remote management.',
    content: 'Full article content here...',
    category: 'launches',
    author: 'Mike Chen',
    publishedAt: '2026-08-10',
    readTime: '4 min read',
    featured: true,
    tags: ['Irrigation', 'Valley', 'New Products'],
  },
  {
    id: '3',
    title: 'Harvest Season Preparation Guide 2026',
    slug: 'harvest-season-preparation-guide-2026',
    excerpt: 'Essential maintenance checks and preparation tips to ensure your harvesting equipment runs smoothly throughout the season.',
    content: 'Full article content here...',
    category: 'tips',
    author: 'David Rodriguez',
    publishedAt: '2026-08-05',
    readTime: '8 min read',
    featured: false,
    tags: ['Harvesting', 'Maintenance', 'Guide'],
  },
  {
    id: '4',
    title: 'SIAEDTS-SARL Hosts Annual Agricultural Technology Expo',
    slug: 'annual-ag-tech-expo-2026',
    excerpt: 'Join us for our biggest event of the year featuring live equipment demos, expert seminars, and exclusive show pricing.',
    content: 'Full article content here...',
    category: 'events',
    author: 'Lisa Thompson',
    publishedAt: '2026-07-28',
    readTime: '3 min read',
    featured: true,
    tags: ['Events', 'Expo', 'Demo Days'],
  },
  {
    id: '5',
    title: 'Sustainable Farming Practices for Modern Agriculture',
    slug: 'sustainable-farming-practices',
    excerpt: 'Explore how precision agriculture and sustainable practices can increase yields while reducing environmental impact.',
    content: 'Full article content here...',
    category: 'industry',
    author: 'Dr. Amanda Foster',
    publishedAt: '2026-07-20',
    readTime: '7 min read',
    featured: false,
    tags: ['Sustainability', 'Precision Ag', 'Environment'],
  },
  {
    id: '6',
    title: 'Case IH Axial-Flow 250 Series Arrives at Our Showroom',
    slug: 'case-ih-axial-flow-250-arrival',
    excerpt: 'The most advanced combine harvester in the industry is now available for viewing and test drives at our main facility.',
    content: 'Full article content here...',
    category: 'launches',
    author: 'Mike Chen',
    publishedAt: '2026-07-15',
    readTime: '4 min read',
    featured: false,
    tags: ['Case IH', 'Harvesting', 'New Arrival'],
  },
  {
    id: '7',
    title: 'Financing Options for Agricultural Equipment',
    slug: 'financing-options-agricultural-equipment',
    excerpt: 'Learn about flexible financing solutions tailored for farmers, including seasonal payment plans and low-interest options.',
    content: 'Full article content here...',
    category: 'tips',
    author: 'James Wilson',
    publishedAt: '2026-07-10',
    readTime: '6 min read',
    featured: false,
    tags: ['Financing', 'Business', 'Guide'],
  },
  {
    id: '8',
    title: 'SIAEDTS-SARL Celebrates 15 Years of Service',
    slug: '15-years-anniversary',
    excerpt: 'A look back at our journey from a small dealership to a leading agricultural equipment provider in the region.',
    content: 'Full article content here...',
    category: 'company',
    author: 'Sarah Johnson',
    publishedAt: '2026-07-01',
    readTime: '5 min read',
    featured: true,
    tags: ['Anniversary', 'Company History', 'Milestone'],
  },
];

export function News() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [filteredNews, setFilteredNews] = useState<NewsArticle[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;

    async function loadPublishedNews() {
      try {
        const remoteNews = await db.news.getAll({ status: 'published' });
        if (!active) return;

        const mappedNews: NewsArticle[] = (remoteNews as unknown as RemoteNewsArticle[]).map((article) => ({
          id: article.id,
          title: article.title,
          slug: article.slug,
          excerpt: article.excerpt ?? '',
          content: article.content,
          category: article.category ?? 'company',
          author: article.author?.name ?? 'SIAEDTS-SARL',
          publishedAt: article.published_at ?? new Date().toISOString(),
          readTime: '',
          featured: article.featured,
          image: article.featured_image_url ?? undefined,
          tags: [],
        }));
        setArticles(mappedNews);
      } catch {
        if (active) setArticles([]);
      }
    }

    void loadPublishedNews();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let filtered = articles;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(n => n.category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredNews(filtered);
  }, [articles, selectedCategory, searchQuery]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      {/* Page Header */}
      <section className="bg-green-700 text-white py-16 sm:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('news.title')}</h1>
            <p className="text-green-100 text-lg">
              {t('news.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container-main">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2" role="group" aria-label={t('news.categoriesLabel')}>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-green-700 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  aria-pressed={selectedCategory === category}
                >
                  {t(`news.categories.${category}`)}
                </button>
              ))}
            </div>

            {/* Search & View Toggle */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <div className="relative flex-1 max-w-md">
                <label htmlFor="news-search" className="sr-only">{t('common.searchArticles')}</label>
                <input
                  id="news-search"
                  type="search"
                  placeholder={t('common.searchArticles')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input pl-10 pr-4"
                  aria-label={t('common.searchArticles')}
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              <div className="flex items-center space-x-2" role="group" aria-label={t('common.viewMode')}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-pressed={viewMode === 'grid'}
                  aria-label={t('common.gridView')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-pressed={viewMode === 'list'}
                  aria-label={t('common.listView')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Grid/List */}
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              {t('common.showing')} <span className="font-semibold text-gray-900">{filteredNews.length}</span> {t('common.of')} <span className="font-semibold text-gray-900">{articles.length}</span> {t('news.showingArticles')}
            </p>
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" role="list">
              {filteredNews.map((article) => (
                <article key={article.id} className="card group flex flex-col" role="listitem">
                  <Link to={`/news/${article.slug}`} className="block">
                    <div className="aspect-video bg-gray-50 relative overflow-hidden">
                      {article.image ? (
                        <img src={article.image} alt={article.title} className="absolute inset-0 m-auto max-h-full max-w-full object-contain" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                          <span className="badge badge-blue">{t(`news.categories.${article.category}`)}</span>
                      </div>
                      {article.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="badge badge-green">{t('common.featured')}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                        <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
                        <span aria-hidden="true">·</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-700 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">{t('common.by')} {article.author}</span>
                        <span className="text-sm text-green-700 font-medium group-hover:underline">{t('common.readMore')} →</span>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="space-y-4" role="list">
              {filteredNews.map((article) => (
                <article key={article.id} className="card flex flex-col sm:flex-row group" role="listitem">
                  <Link to={`/news/${article.slug}`} className="block w-full sm:w-64 aspect-video bg-gray-50 relative overflow-hidden flex-shrink-0">
                    {article.image ? (
                      <img src={article.image} alt={article.title} className="absolute inset-0 m-auto max-h-full max-w-full object-contain" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="badge badge-blue">{t(`news.categories.${article.category}`)}</span>
                    </div>
                    {article.featured && (
                      <div className="absolute top-3 right-3">
                        <span className="badge badge-green">{t('common.featured')}</span>
                      </div>
                    )}
                  </Link>
                  <div className="p-6 flex flex-col justify-between sm:flex-1">
                    <div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                        <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
                        <span aria-hidden="true">·</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
                        <Link to={`/news/${article.slug}`}>{article.title}</Link>
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                      <div className="flex flex-wrap gap-2">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="badge badge-gray text-xs">{tag}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4 sm:mt-0 sm:border-t-0 sm:border-l sm:pl-6">
                      <span className="text-sm text-gray-500">By {article.author}</span>
                      <Link to={`/news/${article.slug}`} className="btn-primary text-sm py-2 px-4">
                        Read Article
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {filteredNews.length === 0 && (
            <div className="text-center py-16">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters or search terms</p>
              <button
                onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
                className="btn-outline"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-10 flex items-center justify-center space-x-2">
            <button className="btn-secondary px-4 py-2" disabled aria-label="Previous page">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button className="btn-primary px-4 py-2" aria-label="Page 1, current page">1</button>
            <button className="btn-secondary px-4 py-2" aria-label="Page 2">2</button>
            <button className="btn-secondary px-4 py-2" aria-label="Page 3">3</button>
            <span className="px-2 text-gray-400">...</span>
            <button className="btn-secondary px-4 py-2" aria-label="Page 5">5</button>
            <button className="btn-secondary px-4 py-2" aria-label="Next page">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section bg-green-700">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Stay Informed</h2>
            <p className="text-green-100 text-lg mb-8">
              Subscribe to our newsletter for the latest agricultural insights, product updates, and exclusive offers.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                placeholder="Enter your email"
                className="input flex-1"
                required
                aria-label="Email address for newsletter"
              />
              <button type="submit" className="btn bg-white text-green-700 hover:bg-green-50 px-8">
                Subscribe
              </button>
            </form>
            <p className="text-green-200 text-sm mt-4">No spam, unsubscribe anytime.</p>
          </div>
        </div>
      </section>
    </>
  );
}